/**
 * TrinityEngine - Unified canvas engine for the Trinity System playground.
 *
 * Central origin at pixel (0,0) using Boris the Brave's direct tri-cube formulas.
 * The origin vertex (where the 6 innermost triangles meet) sits at canvas center.
 */

import type { TriCubeCoord, PatternFn, Point, PatternData, TriangleSize } from './types';
import { TRIANGLE_SIZE_PRESETS, TRINITY_PALETTE } from './types';
import { toPixelCentered, fromPixelCentered, orientation, disc, neighbors, isValid } from './tri-cube';

const SQRT3 = Math.sqrt(3);
const BG_COLOR = '#0f172a';
const GRID_COLOR = 'rgba(148,163,184,0.15)';
const HOVER_COLOR = 'rgba(255,255,255,0.25)';
const CROSSHAIR_COLOR = 'rgba(255,255,255,0.5)';
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 10;
const ORIGIN: TriCubeCoord = { a: 1, b: 1, c: 0 }; // up triangle adjacent to origin vertex

export class TrinityEngine {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private triangles: Map<string, { coord: TriCubeCoord; color: string }>;
  private size: TriangleSize;
  private sideLength: number;
  private panX = 0;
  private panY = 0;
  private zoom = 1;
  private hoveredCoord: TriCubeCoord | null = null;
  private gridRadius = 29;
  private isDragging = false;
  private isPainting = false;
  private isErasing = false;
  private lastMouse: Point | null = null;
  private refImage: HTMLImageElement | null = null;
  private refOpacity = 0.4;
  private onHover: ((coord: TriCubeCoord | null) => void) | null = null;
  private activeColor: string;
  private activeTool: 'paint' | 'erase' | 'fill' | 'select' = 'paint';
  private selectedCoords: Set<string> = new Set();
  private dirty = true;
  private rafId: number | null = null;
  private cssWidth = 0;
  private cssHeight = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get 2d context');
    this.ctx = ctx;
    this.triangles = new Map();
    this.size = 'M';
    this.sideLength = TRIANGLE_SIZE_PRESETS['M'];
    this.activeColor = TRINITY_PALETTE.lightBlue;
    this.setupEvents();
    this.startRenderLoop();
  }

  // --- Size ---

  setSize(size: TriangleSize): void {
    this.size = size;
    this.sideLength = TRIANGLE_SIZE_PRESETS[size];
    this.markDirty();
  }

  getSize(): TriangleSize {
    return this.size;
  }

  // --- Active Color ---

  setColor(color: string): void {
    this.activeColor = color;
  }

  getColor(): string {
    return this.activeColor;
  }

  // --- Tool ---

  setTool(tool: 'paint' | 'erase' | 'fill' | 'select'): void {
    this.activeTool = tool;
    if (tool === 'select') {
      // Auto-select all painted triangles
      this.selectedCoords.clear();
      for (const key of this.triangles.keys()) {
        this.selectedCoords.add(key);
      }
    } else {
      this.selectedCoords.clear();
    }
    this.markDirty();
  }

  // --- Grid Radius ---

  setGridRadius(radius: number): void {
    this.gridRadius = Math.max(1, Math.round(radius));
    this.markDirty();
  }

  // --- Paint ---

  paintTriangle(coord: TriCubeCoord, color: string): void {
    if (!isValid(coord)) return;
    this.triangles.set(this.coordKey(coord), { coord, color });
    this.markDirty();
  }

  eraseTriangle(coord: TriCubeCoord): void {
    this.triangles.delete(this.coordKey(coord));
    this.markDirty();
  }

  clear(): void {
    this.triangles.clear();
    this.selectedCoords.clear();
    this.markDirty();
  }

  private floodFill(start: TriCubeCoord, color: string | null): void {
    const startKey = this.coordKey(start);
    const startColor = this.triangles.get(startKey)?.color ?? null;
    if (color === startColor) return;

    const visited = new Set<string>();
    const queue: TriCubeCoord[] = [start];
    visited.add(startKey);

    while (queue.length > 0) {
      const coord = queue.pop()!;
      const key = this.coordKey(coord);
      const existing = this.triangles.get(key)?.color ?? null;
      if (existing !== startColor) continue;

      if (color) {
        this.triangles.set(key, { coord, color });
      } else {
        this.triangles.delete(key);
      }

      for (const nb of neighbors(coord)) {
        const nbKey = this.coordKey(nb);
        if (!visited.has(nbKey) && isValid(nb)) {
          visited.add(nbKey);
          queue.push(nb);
        }
      }
    }
    this.markDirty();
  }

  // --- Pattern ---

  applyPattern(fn: PatternFn, radius?: number): void {
    const r = radius ?? this.gridRadius;
    const coords = disc(ORIGIN, r);
    for (const coord of coords) {
      const color = fn(coord.a, coord.b, coord.c);
      if (color) {
        this.triangles.set(this.coordKey(coord), { coord, color });
      }
    }
    this.markDirty();
  }

  // --- Image Overlay ---

  loadImage(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.refImage = img; this.markDirty(); resolve(); };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
    });
  }

  setImageOpacity(opacity: number): void {
    this.refOpacity = Math.max(0, Math.min(1, opacity));
    this.markDirty();
  }

  clearImage(): void {
    this.refImage = null;
    this.markDirty();
  }

  // --- Export / Import ---

  exportPattern(): PatternData {
    const triangles: PatternData['triangles'] = [];
    for (const { coord, color } of this.triangles.values()) {
      triangles.push({ a: coord.a, b: coord.b, c: coord.c, color });
    }
    return {
      name: '', description: '', category: 'basic', functionBody: null,
      triangles, originA: 0, originB: 0, originC: 0,
    };
  }

  importPattern(data: PatternData): void {
    this.triangles.clear();
    for (const t of data.triangles) {
      const coord: TriCubeCoord = { a: t.a, b: t.b, c: t.c };
      if (isValid(coord)) {
        this.triangles.set(this.coordKey(coord), { coord, color: t.color });
      }
    }
    this.markDirty();
  }

  // --- Hover ---

  setOnHover(cb: (coord: TriCubeCoord | null) => void): void {
    this.onHover = cb;
  }

  // --- Hit Test ---

  private hitTest(mouseX: number, mouseY: number): TriCubeCoord | null {
    const world = this.toWorld(mouseX, mouseY);
    // Boris's pick_tri: pixel (0,0) = origin vertex
    const coord = fromPixelCentered(world.x, world.y, this.sideLength);
    return isValid(coord) ? coord : null;
  }

  // --- Rendering ---

  private markDirty(): void { this.dirty = true; }

  private startRenderLoop(): void {
    const loop = () => {
      if (this.dirty) { this.dirty = false; this.render(); }
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private render(): void {
    const { ctx } = this;
    const w = this.cssWidth;
    const h = this.cssHeight;
    if (w === 0 || h === 0) return;

    // Clear in device pixels (before DPR scale is applied by resize)
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.restore();

    // Apply pan/zoom in CSS pixel space (ctx already scaled by DPR from resize)
    ctx.save();
    ctx.translate(w / 2 + this.panX, h / 2 + this.panY);
    ctx.scale(this.zoom, this.zoom);

    // Reference image
    if (this.refImage) {
      ctx.globalAlpha = this.refOpacity;
      ctx.drawImage(this.refImage, -this.refImage.width / 2, -this.refImage.height / 2);
      ctx.globalAlpha = 1;
    }

    // Grid
    this.drawGrid();

    // Painted triangles
    for (const { coord, color } of this.triangles.values()) {
      this.drawTriangleAtCoord(coord, color);
    }

    // Selection highlight
    if (this.selectedCoords.size > 0) {
      for (const key of this.selectedCoords) {
        const entry = this.triangles.get(key);
        if (entry) {
          this.drawTriangleAtCoord(entry.coord, 'rgba(255,255,255,0.35)');
        }
      }
    }

    // Hover highlight
    if (this.hoveredCoord) {
      this.drawTriangleAtCoord(this.hoveredCoord, HOVER_COLOR);
    }

    ctx.restore();
  }

  private drawOriginMarker(): void {
    const { ctx } = this;
    const len = this.sideLength * 2;
    ctx.strokeStyle = CROSSHAIR_COLOR;
    ctx.lineWidth = 1 / this.zoom;
    ctx.beginPath();
    ctx.moveTo(-len, 0); ctx.lineTo(len, 0);
    ctx.moveTo(0, -len); ctx.lineTo(0, len);
    ctx.stroke();
    // Small circle at vertex
    ctx.beginPath();
    ctx.arc(0, 0, 2 / this.zoom, 0, Math.PI * 2);
    ctx.fillStyle = CROSSHAIR_COLOR;
    ctx.fill();
  }

  private drawTriangleAtCoord(coord: TriCubeCoord, color: string): void {
    const { ctx } = this;
    const center = toPixelCentered(coord, this.sideLength);
    const h = (this.sideLength * SQRT3) / 2;
    const orient = orientation(coord);

    ctx.beginPath();
    if (orient === 'up') {
      ctx.moveTo(center.x, center.y - (2 * h) / 3);
      ctx.lineTo(center.x - this.sideLength / 2, center.y + h / 3);
      ctx.lineTo(center.x + this.sideLength / 2, center.y + h / 3);
    } else {
      ctx.moveTo(center.x, center.y + (2 * h) / 3);
      ctx.lineTo(center.x - this.sideLength / 2, center.y - h / 3);
      ctx.lineTo(center.x + this.sideLength / 2, center.y - h / 3);
    }
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  }

  private drawGrid(): void {
    const { ctx } = this;
    const coords = disc(ORIGIN, this.gridRadius);
    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 0.5 / this.zoom;

    for (const coord of coords) {
      const center = toPixelCentered(coord, this.sideLength);
      const h = (this.sideLength * SQRT3) / 2;
      const orient = orientation(coord);

      ctx.beginPath();
      if (orient === 'up') {
        ctx.moveTo(center.x, center.y - (2 * h) / 3);
        ctx.lineTo(center.x - this.sideLength / 2, center.y + h / 3);
        ctx.lineTo(center.x + this.sideLength / 2, center.y + h / 3);
      } else {
        ctx.moveTo(center.x, center.y + (2 * h) / 3);
        ctx.lineTo(center.x - this.sideLength / 2, center.y - h / 3);
        ctx.lineTo(center.x + this.sideLength / 2, center.y - h / 3);
      }
      ctx.closePath();
      ctx.stroke();
    }
  }

  // --- Coordinate helpers ---

  private coordKey(coord: TriCubeCoord): string {
    return `${coord.a},${coord.b},${coord.c}`;
  }

  // --- Transform helpers ---
  // World space: pixel (0,0) = origin vertex, at canvas center.
  // Screen space: CSS pixels from top-left of canvas element.

  private toWorld(screenX: number, screenY: number): Point {
    return {
      x: (screenX - this.cssWidth / 2 - this.panX) / this.zoom,
      y: (screenY - this.cssHeight / 2 - this.panY) / this.zoom,
    };
  }

  // --- Events ---

  private setupEvents(): void {
    const canvas = this.canvas;

    // Disable context menu on canvas for right-click erase
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    canvas.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.shiftKey || e.button === 1) {
        this.isDragging = true;
        this.lastMouse = { x: e.offsetX, y: e.offsetY };
        return;
      }

      const isErase = e.button === 2 || this.activeTool === 'erase';
      const coord = this.hitTest(e.offsetX, e.offsetY);

      if (this.activeTool === 'select') {
        if (coord) {
          const key = this.coordKey(coord);
          if (this.triangles.has(key)) {
            // Toggle: if already selected, deselect; otherwise select
            if (this.selectedCoords.has(key)) {
              this.selectedCoords.delete(key);
            } else {
              this.selectedCoords.add(key);
            }
          }
          this.markDirty();
        }
        return;
      }

      if (this.activeTool === 'fill') {
        if (coord) this.floodFill(coord, isErase ? null : this.activeColor);
        return;
      }

      // paint or erase (including right-click erase)
      this.isPainting = true;
      this.isErasing = isErase;
      if (coord) {
        if (isErase) this.eraseTriangle(coord);
        else this.paintTriangle(coord, this.activeColor);
      }
    });

    canvas.addEventListener('mousemove', (e: MouseEvent) => {
      if (this.isDragging && this.lastMouse) {
        this.panX += e.offsetX - this.lastMouse.x;
        this.panY += e.offsetY - this.lastMouse.y;
        this.lastMouse = { x: e.offsetX, y: e.offsetY };
        this.markDirty();
        return;
      }

      if (this.isPainting) {
        const coord = this.hitTest(e.offsetX, e.offsetY);
        if (coord) {
          if (this.isErasing) this.eraseTriangle(coord);
          else this.paintTriangle(coord, this.activeColor);
        }
        return;
      }

      const coord = this.hitTest(e.offsetX, e.offsetY);
      if (
        coord?.a !== this.hoveredCoord?.a ||
        coord?.b !== this.hoveredCoord?.b ||
        coord?.c !== this.hoveredCoord?.c
      ) {
        this.hoveredCoord = coord;
        this.onHover?.(coord);
        this.markDirty();
      }
    });

    canvas.addEventListener('mouseup', () => {
      this.isDragging = false;
      this.isPainting = false;
      this.isErasing = false;
      this.lastMouse = null;
    });

    canvas.addEventListener('mouseleave', () => {
      this.isDragging = false;
      this.isPainting = false;
      this.isErasing = false;
      this.lastMouse = null;
      if (this.hoveredCoord) {
        this.hoveredCoord = null;
        this.onHover?.(null);
        this.markDirty();
      }
    });

    canvas.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      this.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.zoom * factor));
      this.markDirty();
    }, { passive: false });
  }

  // --- Resize ---

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.cssWidth = rect.width;
    this.cssHeight = rect.height;
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.markDirty();
  }

  // --- Triangle count ---

  get triangleCount(): number {
    return this.triangles.size;
  }

  // --- Export PNG ---

  exportPNG(): string {
    return this.canvas.toDataURL('image/png');
  }

  // --- Export SVG (clipped to painted triangles) ---

  exportSVG(): string | null {
    if (this.triangles.size === 0) return null;

    const h = (this.sideLength * SQRT3) / 2;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const polys: { points: string; color: string }[] = [];

    for (const { coord, color } of this.triangles.values()) {
      const center = toPixelCentered(coord, this.sideLength);
      const orient = orientation(coord);
      let verts: [number, number][];

      if (orient === 'up') {
        verts = [
          [center.x, center.y - (2 * h) / 3],
          [center.x - this.sideLength / 2, center.y + h / 3],
          [center.x + this.sideLength / 2, center.y + h / 3],
        ];
      } else {
        verts = [
          [center.x, center.y + (2 * h) / 3],
          [center.x - this.sideLength / 2, center.y - h / 3],
          [center.x + this.sideLength / 2, center.y - h / 3],
        ];
      }

      for (const [vx, vy] of verts) {
        if (vx < minX) minX = vx;
        if (vy < minY) minY = vy;
        if (vx > maxX) maxX = vx;
        if (vy > maxY) maxY = vy;
      }

      polys.push({
        points: verts.map(([vx, vy]) => `${vx},${vy}`).join(' '),
        color,
      });
    }

    // Shift all points so the SVG origin is at the bounding box top-left
    const pad = this.sideLength * 0.1;
    const ox = minX - pad;
    const oy = minY - pad;
    const width = maxX - minX + pad * 2;
    const height = maxY - minY + pad * 2;

    const shifted = polys.map(({ points, color }) => {
      const pts = points.split(' ').map((p) => {
        const [x, y] = p.split(',').map(Number);
        return `${(x - ox).toFixed(2)},${(y - oy).toFixed(2)}`;
      }).join(' ');
      return `  <polygon points="${pts}" fill="${color}"/>`;
    });

    return [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${width.toFixed(2)}" height="${height.toFixed(2)}" viewBox="0 0 ${width.toFixed(2)} ${height.toFixed(2)}">`,
      ...shifted,
      '</svg>',
    ].join('\n');
  }

  // --- Cleanup ---

  destroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
}
